import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import { encodeOpenMetadataFqn } from '@/shared/lib/format';

export type GlossaryTermDetail = {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  fullyQualifiedName: string;
  synonyms: string[];
  glossary: {
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    description?: string;
    displayName: string;
    deleted: boolean;
  };
  children?: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    description?: string;
    displayName: string;
    deleted: boolean;
  }>;
  relatedTerms: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
    deleted: boolean;
  }>;
  references: Array<{
    name: string;
    endpoint: string;
  }>;
  reviewers: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
    deleted: boolean;
  }>;
  owners: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    displayName: string;
    deleted: boolean;
  }>;
  tags: Array<{
    tagFQN: string;
    name: string;
    displayName?: string;
    description?: string;
    source: string;
    labelType: string;
    state: string;
  }>;
  domains?: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    description?: string;
    displayName: string;
    inherited?: boolean;
  }>;
  status: string;
  deleted: boolean;
  provider: string;
  mutuallyExclusive: boolean;
  votes?: {
    upVotes: number;
    downVotes: number;
    upVoters: Array<{ id: string }>;
    downVoters: Array<{ id: string }>;
  };
  childrenCount?: number;
  version: number;
  updatedAt: number;
  updatedBy: string;
};

export type GlossaryTermDetailResponse = GlossaryTermDetail;

export const fetchGlossaryTermDetail = async (termFqn: string): Promise<GlossaryTermDetailResponse> => {
  try {
    const encodedFqn = encodeOpenMetadataFqn(termFqn);
    const response = await openMetadataApiClient.get<GlossaryTermDetailResponse>(`/glossaryTerms/name/${encodedFqn}`, {
      params: {
        fields: 'relatedTerms,reviewers,tags,owners,children,votes,domains,extension,childrenCount',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch glossary term detail:', error);
    throw error;
  }
};

export type ChildTermsResponse = {
  data: Array<{
    id: string;
    name: string;
    displayName: string;
    description?: string;
    fullyQualifiedName: string;
    synonyms: string[];
    relatedTerms: Array<{
      id: string;
      type: string;
      name: string;
      fullyQualifiedName: string;
      displayName: string;
      deleted: boolean;
    }>;
    owners: Array<{
      id: string;
      type: string;
      name: string;
      displayName: string;
    }>;
    childrenCount?: number;
    status: string;
  }>;
  paging: {
    total: number;
  };
};

export const fetchChildTerms = async (parentFqn: string): Promise<ChildTermsResponse> => {
  try {
    const response = await openMetadataApiClient.get<ChildTermsResponse>('/glossaryTerms', {
      params: {
        directChildrenOf: parentFqn,
        fields: 'childrenCount,owners,reviewers,relatedTerms',
        limit: 50,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch child terms:', error);
    throw error;
  }
};

type TaggedAssetsCountResponse = {
  hits: {
    total: {
      value: number;
    };
  };
};

export const fetchTaggedAssetsCount = async (termFqn: string): Promise<number> => {
  try {
    const queryFilter = {
      query: {
        bool: {
          must: [{ term: { 'tags.tagFQN': termFqn } }],
        },
      },
    };

    const response = await openMetadataApiClient.get<TaggedAssetsCountResponse>('/search/query', {
      params: {
        q: '',
        index: 'all',
        from: 0,
        size: 0,
        deleted: false,
        query_filter: JSON.stringify(queryFilter),
      },
      paramsSerializer: {
        encode: (value) => encodeURIComponent(value),
      },
    });
    return response.data.hits.total.value;
  } catch (error) {
    console.error('Failed to fetch tagged assets count:', error);
    return 0;
  }
};
